import React, { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { useStudents } from '../../contexts/StudentsContext';
import { GENDER_OPTIONS, type Gender, type StudentFormValues, emptyStudentForm } from '../../types/student';
import { describeSupabaseError } from '../../utils/supabase';
const UNSPECIFIED = 'unspecified';
export function StudentFormModal() {
  const {
    formOpen,
    editing,
    closeForm,
    createStudent,
    updateStudent
  } = useStudents();
  const [values, setValues] = useState<StudentFormValues>(emptyStudentForm);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!formOpen) return;
    if (editing) {
      setValues({
        fullNames: editing.fullNames,
        gender: editing.gender,
        className: editing.className,
        fatherName: editing.fatherName,
        fatherPhone: editing.fatherPhone,
        motherName: editing.motherName,
        motherPhone: editing.motherPhone,
        note: editing.note
      });
    } else {
      setValues(emptyStudentForm);
    }
  }, [formOpen, editing]);
  const isEmptyRecord = useMemo(() => Object.values(values).every(value => !String(value).trim()), [values]);
  const update = (key: keyof StudentFormValues, value: string) => setValues(current => ({
    ...current,
    [key]: value
  }));
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isEmptyRecord && !editing) {
      toast.warning('Add at least one detail before saving.');
      return;
    }
    setSaving(true);
    try {
      const label = values.fullNames.trim() || 'Untitled record';
      if (editing) {
        await updateStudent(editing, values);
        toast.success('Student record updated', {
          description: label
        });
      } else {
        await createStudent(values);
        toast.success('Student record saved', {
          description: label
        });
      }
      closeForm();
    } catch (error) {
      toast.error('Could not save the record', {
        description: describeSupabaseError(error)
      });
    } finally {
      setSaving(false);
    }
  };
  return <Dialog open={formOpen} onOpenChange={open => open ? undefined : closeForm()} data-id="element-281" data-mp="11soz3xgsjarm">
      <DialogContent className="glass-strong max-h-[92vh] max-w-2xl overflow-y-auto scrollbar-slim rounded-3xl" data-id="element-282" data-mp="16jngz1hfggwz">
        <DialogHeader data-id="element-283" data-mp="1bjjm541v2ee4">
          <DialogTitle className="flex items-center gap-2 text-lg" data-id="element-284" data-mp="1biqmso58er60">
            <span className="grid h-9 w-9 place-items-center rounded-xl brand-gradient text-white" data-id="element-285" data-mp="1flmyduwctn0g">
              <UserPlus className="h-4 w-4" data-id="element-286" data-mp="1kj6phve5hu9t" />
            </span>
            {editing ? 'Edit student record' : 'Add a new student'}
          </DialogTitle>
          <DialogDescription data-id="element-287" data-mp="34zwehzh8u3e6">
            Every field is optional — save what you have now and complete the rest later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-2" data-id="element-288" data-mp="2w1uf1od5rit0">
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-289" data-mp="2r2zmvvtawhw8">
            <div className="space-y-2 sm:col-span-2" data-id="element-290" data-mp="2r4ate3e4s3r1">
              <Label htmlFor="fullNames" data-id="element-291" data-mp="3nrfcfrweur9c">Full names</Label>
              <Input id="fullNames" value={values.fullNames} onChange={event => update('fullNames', event.target.value)} placeholder="e.g. Aline Uwase" className="h-10 rounded-xl" autoComplete="off" data-id="element-292" data-mp="3jqvgusrhjvpb" />
            </div>

            <div className="space-y-2" data-id="element-293" data-mp="bbbqq2m42teh">
              <Label htmlFor="className" data-id="element-294" data-mp="bceisdv671vz">Class</Label>
              <Input id="className" value={values.className} onChange={event => update('className', event.target.value)} placeholder="e.g. Primary 4B" className="h-10 rounded-xl" autoComplete="off" data-id="element-295" data-mp="6k3hb6ks781b" />
            </div>

            <div className="space-y-2" data-id="element-296" data-mp="3db4sbftvkmz7">
              <Label htmlFor="gender" data-id="element-297" data-mp="3i8n5jiexdod0">Gender</Label>
              <Select value={values.gender || UNSPECIFIED} onValueChange={next => update('gender', (next === UNSPECIFIED ? '' : next) as Gender)} data-id="element-298" data-mp="3i9g5o0jdpfm6">
                <SelectTrigger id="gender" className="h-10 w-full rounded-xl" data-id="element-299" data-mp="dt58id18075u">
                  <SelectValue placeholder="Select gender" data-id="element-300" data-mp="3tlr7z26vby" />
                </SelectTrigger>
                <SelectContent data-id="element-301" data-mp="51bm49z0g28c">
                  <SelectItem value={UNSPECIFIED} data-id="element-302" data-mp="50k3dzqihyzh">Not specified</SelectItem>
                  {GENDER_OPTIONS.map(gender => <SelectItem key={gender} value={gender} data-id="element-303" data-mp="spusgjyi24xs">
                      {gender}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-id="element-304" data-mp="12vw39427h6zz">
              <Label htmlFor="fatherName" data-id="element-305" data-mp="12uutr40u8skd">Father&apos;s name</Label>
              <Input id="fatherName" value={values.fatherName} onChange={event => update('fatherName', event.target.value)} placeholder="Father's full name" className="h-10 rounded-xl" autoComplete="off" data-id="element-306" data-mp="1gnkfd7cgfvgy" />
            </div>

            <div className="space-y-2" data-id="element-307" data-mp="jycrvts9zcae">
              <Label htmlFor="fatherPhone" data-id="element-308" data-mp="xwjhkmn2m5yz">Father&apos;s telephone</Label>
              <Input id="fatherPhone" type="tel" value={values.fatherPhone} onChange={event => update('fatherPhone', event.target.value)} placeholder="+250 7xx xxx xxx" className="h-10 rounded-xl" autoComplete="off" data-id="element-309" data-mp="xxumwqw5zls7" />
            </div>

            <div className="space-y-2" data-id="element-310" data-mp="2cc9nk92p9znq">
              <Label htmlFor="motherName" data-id="element-311" data-mp="2cfdx7lyxpnls">Mother&apos;s name</Label>
              <Input id="motherName" value={values.motherName} onChange={event => update('motherName', event.target.value)} placeholder="Mother's full name" className="h-10 rounded-xl" autoComplete="off" data-id="element-312" data-mp="2gdlfil91qwz3" />
            </div>

            <div className="space-y-2" data-id="element-313" data-mp="1snhcog3on195">
              <Label htmlFor="motherPhone" data-id="element-314" data-mp="1xnn9sbsq1fym">Mother&apos;s telephone</Label>
              <Input id="motherPhone" type="tel" value={values.motherPhone} onChange={event => update('motherPhone', event.target.value)} placeholder="+250 7xx xxx xxx" className="h-10 rounded-xl" autoComplete="off" data-id="element-315" data-mp="22ni12gges7k7" />
            </div>

            <div className="space-y-2 sm:col-span-2" data-id="element-316" data-mp="1ew2u2adot22m">
              <Label htmlFor="note" data-id="element-317" data-mp="17exuffprgwlr">Small note</Label>
              <Textarea id="note" value={values.note} onChange={event => update('note', event.target.value)} placeholder="Allergies, transport arrangement, anything worth remembering…" rows={3} className="rounded-xl" data-id="element-318" data-mp="17cbplnu98v78" />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-[var(--glass-hairline)] pt-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-319" data-mp="oglk3qn45gol">
            <p className="text-xs text-muted-foreground" data-id="element-320" data-mp="jmzay8g5ruxj">Partial records are saved cleanly — nothing is required.</p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row" data-id="element-321" data-mp="9qw810dhfs9j">
              <Button type="button" variant="ghost" className="h-10 rounded-xl" onClick={closeForm} disabled={saving} data-id="element-322" data-mp="9ojio25eafmg">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="h-10 rounded-xl brand-gradient px-6 text-white shadow-lg shadow-indigo-500/25 hover:opacity-95" data-id="element-323" data-mp="r92q3hmu2b7">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-324" data-mp="1tje6v7ow5bxz" /> : <Check className="mr-2 h-4 w-4" data-id="element-325" data-mp="1eng3p5gjv7c9" />}
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Submit record'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>;
}